import React from "react";
import { Link } from "react-router-dom";
import { Mail, Globe, Linkedin, ArrowUpRight, Settings, Github, Instagram, Twitter, Facebook, MessageCircle, MapPin } from "lucide-react";
import Logo from "@/components/Logo";
import { INDUSTRIES } from "@/data/site";

export default function Footer() {
  const year = new Date().getFullYear();
  return (
    <footer className="bg-[#0A1628] text-white pt-24 pb-10 border-t border-white/10 relative overflow-hidden" data-testid="site-footer">
      <div className="absolute inset-0 bg-dot-hero opacity-60 pointer-events-none" />
      <div className="relative px-6 sm:px-10 lg:px-16 xl:px-20">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-12 gap-x-8 gap-y-12">

          {/* Brand */}
          <div className="col-span-2 md:col-span-3 lg:col-span-2 space-y-5">
            <Logo className="h-11" onDark />
            <div className="space-y-2 text-sm">
              <a href="mailto:info@kibalabs.in" className="flex items-center gap-3 text-white/80 hover:text-[#FFC93A] transition-colors" data-testid="footer-email-link">
                <Mail className="w-4 h-4 flex-shrink-0" /> info@kibalabs.in
              </a>
              <a href="https://www.kibalabs.in" target="_blank" rel="noreferrer" className="flex items-center gap-3 text-white/80 hover:text-[#FFC93A] transition-colors">
                <Globe className="w-4 h-4 flex-shrink-0" /> www.kibalabs.in
              </a>
              <div className="flex items-start gap-3 text-white/80 pt-1">
                <MapPin className="w-4 h-4 flex-shrink-0 mt-0.5" />
                <span className="text-sm leading-relaxed">
                  <span className="font-semibold text-white/90">HQ:</span> Wework, BLOCK D3,<br />
                  Manyata Tech Park Rd, Thanisandra,<br />
                  Bengaluru Urban, Karnataka-560045<br />
                  <br />
                  STPI Building, New Patliputra Colony,<br />
                  Patna, Bihar-800013
                </span>
              </div>
            </div>

            {/* Social icons */}
            <div className="flex items-center gap-2 pt-1">
              {[
                { href: "https://www.linkedin.com/company/79855022/",   Icon: Linkedin,       label: "LinkedIn" },
                { href: "https://x.com/KIBA_India",                      Icon: Twitter,        label: "X / Twitter" },
                { href: "https://www.facebook.com/kibalabs.in/",         Icon: Facebook,       label: "Facebook" },
                { href: "https://www.instagram.com/kibalabs",            Icon: Instagram,      label: "Instagram" },
                { href: "https://github.com/KIBA-LABS",                  Icon: Github,         label: "GitHub" },
                {
                  href: "https://api.whatsapp.com/send/?phone=8434070785&text=I%27m+inquiring+about+the+KIBA+Labs+Services+and+Products&type=phone_number&app_absent=0",
                  Icon: MessageCircle,
                  label: "WhatsApp",
                },
              ].map(({ href, Icon, label }) => (
                <a
                  key={label}
                  href={href}
                  target="_blank"
                  rel="noreferrer"
                  aria-label={label}
                  className="w-8 h-8 rounded-full border border-white/25 flex items-center justify-center text-white/80 hover:text-[#FFC93A] hover:border-[#FFC93A]/50 transition-all duration-200"
                >
                  <Icon className="w-3.5 h-3.5" />
                </a>
              ))}
            </div>
          </div>

          {/* Company */}
          <div className="lg:col-span-2">
            <div className="text-xs uppercase tracking-[0.22em] text-[#FFC93A] mb-5 font-medium">Company</div>
            <ul className="space-y-3 text-sm">
              <li><Link to="/about"   className="text-white/80 hover:text-[#FFC93A] transition-colors">About</Link></li>
              <li><Link to="/partner" className="text-white/80 hover:text-[#FFC93A] transition-colors">Partner</Link></li>
              <li><Link to="/careers" className="text-white/80 hover:text-[#FFC93A] transition-colors">Careers</Link></li>
              <li><Link to="/blog"    className="text-white/80 hover:text-[#FFC93A] transition-colors">Blog</Link></li>
              <li><Link to="/contact" className="text-white/80 hover:text-[#FFC93A] transition-colors">Contact</Link></li>
            </ul>
          </div>

          {/* Capabilities */}
          <div className="lg:col-span-2">
            <div className="text-xs uppercase tracking-[0.22em] text-[#FFC93A] mb-5 font-medium">Capabilities</div>
            <ul className="space-y-3 text-sm">
              <li><Link to="/services" className="text-white/80 hover:text-[#FFC93A] transition-colors">SAP BTP & CAP</Link></li>
              <li><Link to="/services" className="text-white/80 hover:text-[#FFC93A] transition-colors">SAP ABAP & RAP</Link></li>
              <li><Link to="/services" className="text-white/80 hover:text-[#FFC93A] transition-colors">Cloud & DevOps</Link></li>
              <li><Link to="/services" className="text-white/80 hover:text-[#FFC93A] transition-colors">GenAI & ML</Link></li>
              <li><Link to="/services" className="text-white/80 hover:text-[#FFC93A] transition-colors">Full-Stack</Link></li>
              <li><Link to="/services" className="text-white/80 hover:text-[#FFC93A] transition-colors">Blockchain & DeFi</Link></li>
            </ul>
          </div>

          {/* Industries */}
          <div className="lg:col-span-2">
            <div className="text-xs uppercase tracking-[0.22em] text-[#FFC93A] mb-5 font-medium">Industries</div>
            <ul className="space-y-3 text-sm">
              {INDUSTRIES.map((ind) => (
                <li key={ind.slug}>
                  <Link to="/industries" className="text-white/80 hover:text-[#FFC93A] transition-colors">
                    {ind.title}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Products */}
          <div className="lg:col-span-2">
            <div className="text-xs uppercase tracking-[0.22em] text-[#FFC93A] mb-5 font-medium">Products</div>
            <ul className="space-y-4 text-sm">
              {[
                { name: "AIDIPH",       desc: "AI-driven HCM & Payroll" },
                { name: "IndentLedger", desc: "Hardware indent & supply ledger" },
                { name: "Proctorly",    desc: "AI-embedded proctor SaaS" },
                { name: "One SAP",      desc: "Conversational SAP intelligence" },
              ].map((p) => (
                <li key={p.name}>
                  <Link to="/products" className="group flex flex-col gap-0.5">
                    <span className="flex items-center gap-1.5 font-semibold text-white/80 group-hover:text-[#FFC93A] transition-colors">
                      {p.name}
                      <span className="text-[9px] uppercase tracking-wider bg-[#FFC93A]/15 text-[#FFC93A] px-1.5 py-0.5 font-bold">AI</span>
                    </span>
                    <span className="text-[11px] text-white/75 group-hover:text-[#FFC93A]/80 transition-colors leading-relaxed">{p.desc}</span>
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* CTA */}
          <div className="lg:col-span-2">
            <div className="text-xs uppercase tracking-[0.22em] text-[#FFC93A] mb-5 font-medium">Work with us</div>
            <ul className="space-y-2 mb-5">
              {[
                "Where SAP meets intelligence",
                "Engineering, Cloud, and AI",
                "For enterprises & global SIs",
                "Pre-vetted, payroll engineers",
                "Deploy in 1–2 weeks",
              ].map((item) => (
                <li key={item} className="flex items-start gap-2 text-sm text-white/80">
                  <span className="w-1 h-1 rounded-full bg-[#FFC93A] flex-shrink-0 mt-2" />
                  {item}
                </li>
              ))}
            </ul>
            <Link
              to="/partner"
              className="inline-flex items-center gap-2 bg-[#FFC93A] text-[#0A1628] px-5 py-3 text-sm font-bold hover:bg-white transition-colors whitespace-nowrap"
              data-testid="footer-partner-cta"
            >
              Partner With Us <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
        <div className="mt-16 pt-8 border-t border-white/10 flex flex-col md:flex-row md:items-center md:justify-between gap-4 text-xs text-white/55">
          <div>© {year} KIBA Labs Pvt. Ltd. All rights reserved.</div>
          <div className="flex items-center gap-6">
            <span>Engineered in India · Operating across 10+ countries</span>
            <Link
              to="/admin/login"
              className="inline-flex items-center gap-1.5 text-white/30 hover:text-white/60 transition-colors"
              title="Admin login"
            >
              <Settings className="w-3 h-3" /> Admin
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
